import { Link } from "@tanstack/react-router";
import { ArrowUpRight, Clock } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useLanguage } from "@/i18n/useLanguage";
import type { BlogPostSummary } from "@/lib/blog.functions";

export function ArticleCard({ post, featured = false }: { post: BlogPostSummary; featured?: boolean }) {
  const { t } = useTranslation();
  const { lang } = useLanguage();
  const to = lang === "en" ? "/en/knowledge/$slug" : "/kunskap/$slug";
  return (
    <Link
      to={to}
      params={{ slug: post.slug }}
      className={`group block overflow-hidden rounded-3xl border border-white/10 bg-surface transition-all hover:-translate-y-[2px] hover:border-mint/40 ${
        featured ? "md:grid md:grid-cols-[1.1fr_1fr]" : ""
      }`}
    >
      <div
        className={`relative flex items-center justify-center bg-gradient-to-br from-background to-surface-elevated p-10 ${
          featured ? "min-h-[260px]" : "min-h-[180px]"
        }`}
      >
        <span className="font-serif text-5xl italic text-mint/40">{post.title.slice(0, 1)}</span>
      </div>
      <div className="p-7 md:p-8">
        <div className="text-[11px] uppercase tracking-widest text-muted-foreground">{post.category}</div>
        <h3 className={`mt-3 text-balance font-semibold leading-tight ${featured ? "text-2xl md:text-3xl" : "text-lg md:text-xl"}`}>
          {post.title}
        </h3>
        <p
          className={`mt-3 text-pretty text-muted-foreground ${
            featured ? "text-base md:text-lg" : "text-sm leading-relaxed"
          }`}
        >
          {post.excerpt}
        </p>
        <div className="mt-5 flex items-center justify-between text-xs text-muted-foreground">
          <span className="inline-flex items-center gap-1.5">
            <Clock className="h-3.5 w-3.5" /> {post.read_minutes} {t("article.minRead", { defaultValue: "min read" })}
          </span>
          <ArrowUpRight className="h-4 w-4 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
        </div>
      </div>
    </Link>
  );
}
